import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ProfileDTO } from '../../dto/ProfileDTO';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CabinetService } from "../../../../cabinet/service/cabinet.service";
import { ERROR_IMAGE_LINK } from "../../../../../util/common/links";

@Component({
  selector: 'app-avatar-icon',
  standalone: true,
  templateUrl: './avatar-icon.component.html',
  styleUrl: './avatar-icon.component.scss',
})
/**
 * Displays a user's avatar if available - otherwise, it displays the user's initials.
 *
 * **Important precondition**: profile must be ready before this component is rendered, either from input or in the {@link CabinetService.profile}
 *
 * - **profile**:
 *    If you pass a {@link Profile} object as `@Input()`, the component will use it directly.
 *    If no `@Input() profile` is provided, the component automatically subscribes to the {@link CabinetService.profile}
 *    to get the current user's profile.
 *
 * - **initialsCssClasses**:
 *    In case you need to modify only initials css block, you can do it by providing {@link initialsCssClasses}
 *
 * - **Css classes**:
 *    You can place your css classes directly on `<app-avatar-icon>` or to a wrapping div, e.g.:
 *    ```html
 *    <app-avatar-icon
 *      class="w-1.875 h-1.875 overflow-hidden rounded-md shrink-0"
 *      data-qa="user-avatar"
 *      [fontSize]="'font-2em'"
 *    >
 *    </app-avatar-icon>
 *    ```
 *
 * **Flow Summary**:
 * 1. If `profile` is given as an input, `ngOnInit()` calls `generateAvatar()` immediately.
 * 2. If `profile` is NOT provided, `ngOnInit()` subscribes to `CabinetService.profile` until the component is destroyed.
 * 3. When a profile is found (either via input or service subscription):
 *    - If `profile.photo` is missing or equals `'unknown'`, it displays initials.
 *    - Otherwise, it displays the user’s avatar image.
 */
export class AvatarIconComponent implements OnInit, OnDestroy, OnChanges {
  private cabinetService = inject(CabinetService);

  @Input() initialsCssClasses: string = 'font-3em';
  @Input() profile: ProfileDTO;

  initials = '';
  showInitials = false;
  errorImageLink = ERROR_IMAGE_LINK;
  private _destroyStream: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.profile ? this.generateAvatar() : this.initProfileSub();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profile && this.profile) {
      this.generateAvatar();
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  onPhotoError(): void {
    this.profile.photo = this.errorImageLink;
  }

  private generateAvatar(): void {
    // if no valid photo exists -> show initials
    this.showInitials = !this.profile.photo || this.profile.photo === 'unknown';

    if (this.showInitials) {
      this.createInitials();
    }
  }

  private createInitials(): void {
    this.initials = '';
    if (this.profile.name) {
      this.initials += this.profile.name.charAt(0).toUpperCase();
    }
    if (!this.initials) {
      this.initials = 'N/A';
    }
  }

  private initProfileSub(): void {
    this.cabinetService.profile
      .pipe(takeUntil(this._destroyStream))
      .subscribe((profile: ProfileDTO) => {
        if (!profile) {
          return;
        }
        this.profile = profile;
        this.generateAvatar();
      });
  }
}
